import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Percent as PercentIcon,
  CurrencyRupee as CurrencyRupeeIcon
} from '@mui/icons-material';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { fetchBookingFilters, fetchAgentSelect } from '../../../services/common';
import AddAgentIncentive from './AddAgentIncentive';
import EditAgentIncentive from './EditAgentIncentive';
import { formatDate, formatCurrencyNoDecimals, bookingTokens, cardSx, cardHeadSx, tableHeadSx, emptyStateSx, primaryButtonSx, ghostButtonSx } from '../bms_helper';

const AgentIncentiveTab = ({ booking, onActionComplete }) => {
  const { openConfirmModal, openSuccessModal, openErrorModal } = useModal();
  const { fetchBookingAgentIncentives, addBookingAgentIncentive, updateBookingAgentIncentive, deleteBookingAgentIncentive } = useBookingDetails();
  
  const [incentives, setIncentives] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIncentive, setEditingIncentive] = useState(null);
  

  // Fetch agents and incentives
  useEffect(() => {
    const fetchData = async () => {
      if (!booking?.id) return;
      
      setLoading(true);
      try {
        // Fetch agents using the correct function
        const agentsData = await fetchAgentSelect();
        setAgents(agentsData || []);
        
        // Fetch incentives (you'll need to implement this in the context)
        const incentivesData = await fetchBookingAgentIncentives(booking.id);
        setIncentives(incentivesData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        openErrorModal('Error', 'Failed to load agent incentives data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [booking?.id, fetchBookingAgentIncentives]);

  const handleAddIncentive = () => {
    setAddDialogOpen(true);
  };

  const handleEditIncentive = (incentive) => {
    setEditingIncentive(incentive);
    setEditDialogOpen(true);
  };

  const handleDeleteIncentive = async (incentive) => {
    openConfirmModal(
      'Delete Agent Incentive',
      `Are you sure you want to delete the incentive for ${incentive.agent_name}? This action cannot be undone.`,
      async () => {
        try {
          await deleteBookingAgentIncentive(booking.id, incentive.id);
          openSuccessModal('Success', 'Agent incentive deleted successfully');
          
          // Refresh incentives
          const incentivesData = await fetchBookingAgentIncentives(booking.id);
          setIncentives(incentivesData || []);
          
          if (onActionComplete) {
            onActionComplete();
          }
        } catch (error) {
          openErrorModal('Error', 'Failed to delete agent incentive');
        }
      }
    );
  };

  const handleAddSuccess = async () => {
    // Refresh incentives
    const incentivesData = await fetchBookingAgentIncentives(booking.id);
    setIncentives(incentivesData || []);
    
    if (onActionComplete) {
      onActionComplete();
    }
  };

  const handleEditSuccess = async () => {
    // Refresh incentives
    const incentivesData = await fetchBookingAgentIncentives(booking.id);
    setIncentives(incentivesData || []);
    
    if (onActionComplete) {
      onActionComplete();
    }
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.value === agentId);
    return agent ? agent.label : 'Unknown Agent';
  };

  const getAgentDisplayName = (incentive) => {
    // First try to get from incentive.agent object (from serializer)
    if (incentive.agent?.name) {
      return incentive.agent.name;
    }
    // Fallback: try to find agent from agents list using agent.id
    if (incentive.agent?.id) {
      const agentName = getAgentName(incentive.agent.id);
      if (agentName !== 'Unknown Agent') {
        return agentName;
      }
    }
    // Fallback: try agent_id field (if available in response)
    if (incentive.agent_id) {
      const agentName = getAgentName(incentive.agent_id);
      if (agentName !== 'Unknown Agent') {
        return agentName;
      }
    }
    // Last resort: check if agent object exists but name is missing
    if (incentive.agent && typeof incentive.agent === 'object') {
      console.warn('Agent object exists but name is missing:', incentive.agent);
    }
    return 'Unknown Agent';
  };

  const getAgentEmail = (incentive) => {
    // First try to get from incentive.agent object
    if (incentive.agent?.email) {
      return incentive.agent.email;
    }
    // Fallback: try to find from agents list using agent.id
    if (incentive.agent?.id) {
      const agent = agents.find(a => a.value === incentive.agent.id);
      if (agent) {
        // Agents from fetchAgentSelect might have email in a different structure
        return agent.email || agent.label || '-';
      }
    }
    // Fallback: try agent_id field
    if (incentive.agent_id) {
      const agent = agents.find(a => a.value === incentive.agent_id);
      if (agent) {
        return agent.email || agent.label || '-';
      }
    }
    return '-';
  };

  const calculateIncentiveAmount = (incentive) => {
    if (incentive.incentive_type === 'amount') {
      return incentive.incentive_value;
    } else {
      // Calculate percentage of total commission
      const totalCommission = booking?.commission_breakdown?.total_commission || 0;
      return (incentive.incentive_value / 100) * totalCommission;
    }
  };

  const truncateDescription = (text, maxLength = 30) => {
    if (!text || text === '-') return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading && incentives.length === 0) {
    return (
      <Paper sx={{ width: '100%', p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading agent incentives...
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={0} sx={{ ...cardSx, width: '100%', overflow: 'hidden' }}>
        <Box sx={cardHeadSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: 14 }}>🏆</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink }}>Agent Incentives</Typography>
          </Box>
          {booking.status !== 'cancelled' && booking.status !== 'draft' && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddIncentive} sx={primaryButtonSx}>Add Incentive</Button>
          )}
        </Box>
        <TableContainer>
          <Table size="small" sx={{ '& thead tr': { background: '#f8fbf9' }, '& th': { padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.55px', color: bookingTokens.subtle, borderBottom: `1.5px solid ${bookingTokens.border}` } }}>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {incentives.length > 0 ? (
                incentives.map((incentive) => (
                  <TableRow key={incentive.id} sx={{ '&:hover td': { bgcolor: '#f2f8f4' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg,${bookingTokens.green},#12a85e)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>{getAgentDisplayName(incentive).trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2)}</Box>
                        <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {getAgentDisplayName(incentive)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getAgentEmail(incentive)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: bookingTokens.green }}>
                        {formatCurrencyNoDecimals(incentive.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {incentive.description && incentive.description.length > 30 ? (
                        <Tooltip title={incentive.description} arrow>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              maxWidth: '300px',
                              cursor: 'help'
                            }}
                          >
                            {truncateDescription(incentive.description)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {incentive.description || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(incentive.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                        <Button size="small" onClick={() => handleEditIncentive(incentive)} sx={ghostButtonSx}>Edit</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 0, border: 'none' }}>
                    <Box sx={emptyStateSx}>
                      <Typography sx={{ fontSize: 40, opacity: 0.4 }}>🎯</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: bookingTokens.muted }}>No agent incentives available.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Agent Incentive Dialog */}
      <AddAgentIncentive
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        booking={booking}
        onSuccess={handleAddSuccess}
        agents={agents}
      />

      {/* Edit Agent Incentive Dialog */}
      <EditAgentIncentive
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingIncentive(null);
        }}
        booking={booking}
        agents={agents}
        incentive={editingIncentive}
        onSuccess={handleEditSuccess}
      />
    </>
  );
};

export default AgentIncentiveTab;
